<template>
  <div>
    <div v-if="model.addressFormatted">
      Please provide contact information for the owner of <b>{{ model.addressFormatted }}</b>.
      <template v-if="hasAssessorContact">
        Owner information was found in the
        <a v-if="safeHref(model.property.url)" :href="safeHref(model.property.url)" target="_blank" rel="noopener noreferrer">assessor record</a><template v-else>assessor record</template>.
        Please make any adjustments if needed.
      </template>
    </div>
    <div v-if="model.loading" class="mb-3">
      <SpinnerView small /> Checking assessor records for owner information. One moment please.
    </div>
    <SurveyComponent v-if="survey" :model="survey" />
  </div>
</template>

<script>
import { safeHref } from '@/safeRendering';
import {LicenseEditorModel} from "@/models/LicenseEditorModel";
import SpinnerView from "@/components/common/SpinnerView.vue";
import {SurveyComponent} from "survey-vue3-ui";
import SurveyModel from "@/models/SurveyModel";

const buildSurveyJson = (required = false) => {
  return {
    textUpdateMode: 'onTyping',
    elements: [
      {
        type: 'comment',
        name: 'names',
        title: 'Name(s)',
        description: 'If more than one, please list each contact\'s name on its own line.',
        isRequired: required
      },
      {
        type: 'comment',
        name: 'address',
        title: 'Mailing Address',
        isRequired: required
      },
      {
        type: 'text',
        inputType: 'text',
        name: 'email',
        title: 'Email',
        textUpdateMode:'onTyping',
        validators: [
          { type: 'email' }
        ],
        autocomplete: 'email',
        isRequired: required
      },
      {
        type: 'text',
        inputType: 'text',
        name: 'phone',
        title: 'Phone Number',
        autocomplete: 'tel',
        validators: [
          {
            type: "regex",
            regex: "^(\\+?1[-.\\s]?)?(\\(?\\d{3}\\)?[-.\\s]?)\\d{3}[-.\\s]?\\d{4}$",
            text: "Enter a valid phone number"
          }
        ],
        textUpdateMode:'onTyping',
        isRequired: required
      },
    ]
  };
};

export default {
  methods: {
    safeHref,
    syncSurveyValue(name, value) {
      if (this.survey && (this.survey.getValue(name) ?? '') !== (value ?? '')) {
        this.survey.setValue(name, value ?? '');
      }
    }
  },
  components: {SurveyComponent, SpinnerView},
  props: {
    model: LicenseEditorModel,
    required: Boolean
  },
  emits: ['submit'],
  data() {
    return { survey: null };
  },
  watch: {
    names(value) { this.syncSurveyValue('names', value); },
    address(value) { this.syncSurveyValue('address', value); }
  },
  mounted() {
    const profilePrefix = `[OwnerRegistrationForm ${this.model?.id || 'new'}]`;
    console.time(`${profilePrefix} create survey`);

    // Create the SurveyJS Model instance
    const survey = SurveyModel.createFromJson(buildSurveyJson(this.required), {
      editable: true
    });

    const onlyDigits = phoneNumber => {
      return phoneNumber?.replace(/[^\d]/g, '');
    }

    // Workaround: Phone input doesn't support value changes after typing, which breaks the enabled/disabled state of
    // the "Next" button. This means we can't use SurveyJS' built-in phone mask setting and instead have to render the
    // formatted phone number after the field is blurred.
    survey.onAfterRenderQuestion.add((_sender, options) => {
      const q = options.question;

      if (q.name !== "phone") return;

      const input = options.htmlElement.querySelector("input");
      if (!input) return;

      if (input.dataset.phoneFormatAttached === "true") return;
      input.dataset.phoneFormatAttached = "true";

      // On focus, ensure display value matches the raw value
      input.addEventListener("focus", () => {
        const raw = onlyDigits(q.value || input.value);
        input.value = raw;

        // Optional, but keeps SurveyJS value aligned while focused
        q.value = raw;
      });

      // On blur, update display value to formatted
      input.addEventListener("blur", () => {
        // Store raw value
        q.value = onlyDigits(input.value);

        // Display formatted value
        input.value = this.formatPhone(input.value);
      });
    });

    console.timeEnd(`${profilePrefix} create survey`);
    console.time(`${profilePrefix} populate survey data`);

    survey.mergeData({
      names: this.names,
      address: this.address,
      email: this.email,
      phone: this.phone
    });

    // Contact fields contain only text, so synchronize them immediately. Echoing
    // assessor defaults back into the contact would turn them into applicant edits
    // and prevent a later property lookup from refreshing those defaults.
    survey.onValueChanged.add((_, { name, value }) => {
      value = value ?? '';
      if (name === 'phone') value = onlyDigits(value);
      if (this[name] !== value) this[name] = value;
    });

    console.timeEnd(`${profilePrefix} populate survey data`);

    this.survey = survey;
  },
  computed: {
    hasAssessorContact() {
      return Boolean(this.model.property &&
        (this.model.contact.defaultOwners.length || this.model.contact.defaultAddress));
    },
    names: {
      get() {
        return this.model.contact.owners.join('\n');
      },
      set(names) {
        this.model.contact.owners = names.split('\n');
      }
    },
    address: {
      get() { return this.model.contact.address; },
      set(v) { this.model.contact.address = v; }
    },
    email: {
      get() { return this.model.contact.email; },
      set(v) { this.model.contact.email = v; }
    },
    phone: {
      get() { return this.model.contact.phone; },
      set(v) { this.model.contact.phone = v; }
    },
  }
}
</script>
